import { Button, Heading, Inline, useStyledSystem } from "@hopper-ui/components";
import { AngleRightIcon, createIcon } from "@hopper-ui/icons";
import { useLocalStorage } from "@uidotdev/usehooks";
import { type PropsWithChildren, type Ref, type SVGProps, forwardRef } from "react";
import { Disclosure, DisclosurePanel } from "react-aria-components";
import { useShellTranslation } from "../i18n/i18next.ts";
import { ColorSchemeToggleTool } from "./ColorSchemeToggleTool.tsx";
import { FeatureFlagPickerTool } from "./FeatureFlagPickerTool.tsx";
import { MswScenariosConfigurator } from "./MswScenariosConfigurator.tsx";
import { ReactQueryDevTool } from "./ReactQueryDevTool.tsx";
import { RegisteredDevTools } from "./registerProtectDevTool.tsx";
import { ResetLocalStorageTool } from "./ResetLocalStorageTool.tsx";

export const DevTools = () => {
    return (
        <ExpandableDevToolPanel>
            <RegisteredDevTools />
            <ColorSchemeToggleTool />
            <ResetLocalStorageTool />
            <FeatureFlagPickerTool />
            <MswScenariosConfigurator />
            <ReactQueryDevTool />
        </ExpandableDevToolPanel>
    );
};

const LOCAL_STORAGE_KEY = "devtools-expanded";
function ExpandableDevToolPanel({ children }: PropsWithChildren) {
    const { t } = useShellTranslation();
    const [isExpanded, setIsExpanded] = useLocalStorage(LOCAL_STORAGE_KEY, false);

    const disclosureProps = useStyledSystem({
        display: "flex",
        position: "fixed",
        bottom: "12px",
        right: "0px",
        zIndex: 20,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "neutral-weak",
        border: "neutral",
        borderTopLeftRadius: "rounded-md",
        borderBottomLeftRadius: "rounded-md",
        borderTopRightRadius: "core_0",
        borderBottomRightRadius: "core_0",
        height: "core_640",
        transition: "all 200ms"
    });

    const disclosurePanelProps = useStyledSystem({
        UNSAFE_width: "var(--disclosure-panel-width)", // react-aria sets this variable automatically
        transition: "width 250ms",
        overflowX: "clip"
    });

    return (
        <Disclosure {...disclosureProps.stylingProps} isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
            <Heading height="100%">
                <Button
                    slot="trigger"
                    variant="ghost-secondary"
                    size="sm"
                    height="100%"
                    aspectRatio="unset"
                    borderTopRightRadius="core_0"
                    borderBottomRightRadius="core_0"
                    borderRight="neutral"
                    aria-label={isExpanded ? t("DevTools.collapse") : t("DevTools.expand")}
                >
                    {isExpanded ? <AngleRightIcon size="sm" /> : <ToolIcon16 />}
                </Button>
            </Heading>
            <DisclosurePanel {...disclosurePanelProps.stylingProps}>
                <Inline gap="inline-sm" wrap={false} paddingX="inset-sm">
                    {children}
                </Inline>
            </DisclosurePanel>
        </Disclosure>
    );
}

const ToolIcon16Src = forwardRef((props: SVGProps<SVGSVGElement>, ref: Ref<SVGSVGElement>) => (
    <svg width={16} height={16} viewBox="0 0 24 24" ref={ref} {...props}>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4,24c-1.1,0-2.1-0.4-2.8-1.2C0.5,22.1,0,21,0,20s0.4-2.1,1.2-2.8l8-8C9.1,8.7,9,8.1,9,7.5C9,3.4,12.4,0,16.5,0    c0.9,0,1.8,0.2,2.6,0.5L20.6,1L16,5.6L18.4,8L23,3.4l0.6,1.4C23.8,5.7,24,6.6,24,7.5c0,4.1-3.4,7.5-7.5,7.5    c-0.6,0-1.1-0.1-1.7-0.2l-8,8C6.1,23.6,5.1,24,4,24z M16.5,2c-3,0-5.5,2.5-5.5,5.5c0,0.6,0.1,1.1,0.3,1.7l0.2,0.6l-8.9,8.9    C2.2,19,2,19.5,2,20s0.2,1,0.6,1.4c0.7,0.8,2.1,0.8,2.8,0l8.9-8.9l0.6,0.2c0.5,0.2,1.1,0.3,1.7,0.3c3,0,5.5-2.5,5.5-5.5    c0-0.1,0-0.2,0-0.3l-3.6,3.6l-5.2-5.2L16.9,2C16.7,2,16.6,2,16.5,2z"
        />
    </svg>
));
const ToolIcon16 = createIcon(ToolIcon16Src, ToolIcon16Src, ToolIcon16Src, "ToolIcon");
